import * as React from "react";
import {useJsonForms, withJsonFormsControlProps} from "@jsonforms/react";
import Autocomplete from "./Autocomplete";
import {SchemaUtils} from "../../util/SchemaUtils";
import {ControlValueFactory} from "../../factory/ControlValueFactory";
import {Component} from "../../enum/Component";
import {useMemo, useCallback} from 'react';

interface AutocompleteControlProps {
    data: any;
    handleChange: (path: string, value: string | null) => void;
    path: string;
    uischema: any;
    schema: any;
    errors: string;
    rootSchema: any;
    label: string;
    visible?: boolean;
    enabled?: boolean;
    required?: boolean;
    appValidated?: boolean;
}

const AutocompleteControl = React.memo((props: AutocompleteControlProps) => {
    const {
        data,
        handleChange,
        path,
        uischema,
        schema,
        errors,
        label,
        rootSchema,
        appValidated
    } = props;

    const jsonForms = useJsonForms();

    // Memoize expensive computations
    const visible = useMemo(() =>
            SchemaUtils.isComponentVisible(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const enabled = useMemo(() =>
            SchemaUtils.isComponentEnabled(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const required = useMemo(() =>
            SchemaUtils.isRequired(path, rootSchema),
        [path, rootSchema]
    );

    const enumSource = schema.enum ?? schema.anyOf?.[0]?.enum;
    const options = useMemo(() =>
            (Array.isArray(enumSource) ? enumSource : [])
                .filter((option: string) => option !== "" && option != null),
        [enumSource]
    );

    const value = useMemo(() =>
            ControlValueFactory.getControlValue(Component.Autocomplete, data, uischema.scope, jsonForms, enabled && visible),
        [Component.Autocomplete, data, uischema.scope, jsonForms, enabled, visible]
    );

    // Memoize the change handler
    const handleAutocompleteChange = useCallback((value: string | null) => {
        handleChange(path, value);
    }, [handleChange, path]);

    // Memoize the Autocomplete component props
    const autocompleteProps = useMemo(() => ({
        label: label || uischema.label,
        description: schema.description,
        value: value,
        onChange: handleAutocompleteChange,
        options: options,
        required: required,
        error: errors,
        enabled: enabled,
        visible: visible,
        appValidated: appValidated
    }), [
        label,
        uischema.label,
        schema.description,
        value,
        handleAutocompleteChange,
        options,
        required,
        errors,
        enabled,
        visible,
        appValidated
    ]);

    return <Autocomplete {...autocompleteProps} />;
});

AutocompleteControl.displayName = 'AutocompleteControl';

export default withJsonFormsControlProps(AutocompleteControl);
